import type { ContextObject, ContextTypeKind } from '../../components/protected/Search/ContextSelection/types';
import type { PathSegmentContextRef } from '../../infra/protected/Search/SearchResultData';
import { FPADataTypes } from '../../infra/protected/FPA/FPAData';
import type { FPAItem } from '../../infra/protected/FPA/FPAItem';

function segmentTypeFromFpaType(type: FPADataTypes): PathSegmentContextRef['type'] {
    if (type === FPADataTypes.FOLDER || type === FPADataTypes.FOLDER_TYPE) {
        return 'FOLDER';
    }
    if (type === FPADataTypes.PROJECT) {
        return 'PROJECT';
    }
    if (type === FPADataTypes.ACTIVITY) {
        return 'ACTIVITY';
    }
    return 'FOLDER';
}

function leafContextType(leaf: FPAItem): ContextTypeKind {
    if (leaf.type === FPADataTypes.FOLDER) {
        return 'FOLDER';
    }
    if (leaf.type === FPADataTypes.PROJECT) {
        return 'PROJECT';
    }
    if (leaf.type === FPADataTypes.ACTIVITY) {
        return 'ACTIVITY';
    }
    return '';
}

/** Build a {@link ContextObject} from a root-to-leaf chain of {@link FPAItem} nodes. */
export function contextObjectFromFpaPath(path: FPAItem[]): ContextObject {
    if (path.length === 0) {
        return {
            contextId: 0,
            contextType: '',
            link: '',
            pathSegments: [],
        };
    }
    const leaf = path[path.length - 1];
    const pathSegmentContexts: PathSegmentContextRef[] = path.map((node) => ({
        id: node.id,
        type: segmentTypeFromFpaType(node.type),
    }));
    return {
        contextId: leaf.id,
        contextType: leafContextType(leaf),
        link: '',
        pathSegments: path.map((n) => n.title).filter(Boolean),
        pathSegmentContexts,
    };
}

/** Minimal tree row when the node is not present in the loaded finder tree (e.g. filtered). */
export function minimalFpaItemFromContextObject(ctx: ContextObject): FPAItem | null {
    if (ctx.contextId == null || ctx.contextId <= 0) {
        return null;
    }
    const typeMap: Partial<Record<ContextTypeKind, FPADataTypes>> = {
        FOLDER: FPADataTypes.FOLDER,
        PROJECT: FPADataTypes.PROJECT,
        ACTIVITY: FPADataTypes.ACTIVITY,
    };
    const type = typeMap[ctx.contextType];
    if (!type) {
        return null;
    }
    const title =
        ctx.pathSegments.length > 0
            ? ctx.pathSegments[ctx.pathSegments.length - 1]
            : String(ctx.contextId);
    return { id: ctx.contextId, title, type, childItems: [] };
}
